'use client';

import * as React from 'react';
import {ThemeProvider as NextThemesProvider} from 'next-themes';

import {GoogleOAuthProvider} from '@react-oauth/google';
// query client
import QueryProvider from '#components/providers/query-provider';
import ToastProvider from '#components/providers/ToastProvider';
import {UserProvider} from '#contexts/UserContext';
import {AuthProvider} from '#contexts/AuthContext';
import {AuthFormProvider} from '#contexts/AuthFormContext';
import {CookieBannerProvider} from '@purpose/ui/components/cookie-banner';
// cookie utils
import {
  initializeServicesBasedOnConsent,
  disableAnalytics
} from '@purpose/ui/lib/cookie-utils';
import {DateRangeProvider} from '@/src/contexts/DateRangeContext';
import {WorkspaceProvider} from '@/src/contexts/WorkspaceContext';
import {NewBenefitProvider} from '@/src/contexts/NewBenefitContext';
import {UnsavedChangesProvider} from '@/src/contexts/UnsavedChangesContext';

interface ProvidersProps {
  children: React.ReactNode;
  locale?: 'en' | 'no' | 'sv' | 'da';
}

export function Providers({children, locale = 'no'}: ProvidersProps) {
  return (
    <GoogleOAuthProvider
      clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
    >
      <QueryProvider>
        <ToastProvider locale={locale}>
          <AuthProvider>
            <AuthFormProvider>
              <UserProvider>
                <WorkspaceProvider>
                  <DateRangeProvider>
                    <NewBenefitProvider>
                      <NextThemesProvider
                        attribute="class"
                        defaultTheme="light"
                        enableSystem={false}
                        disableTransitionOnChange
                        enableColorScheme
                      >
                        <CookieBannerProvider
                          locale={locale}
                          onAcceptAll={() => {
                            initializeServicesBasedOnConsent();
                          }}
                          onAcceptSelected={() => {
                            initializeServicesBasedOnConsent();
                          }}
                          onDecline={() => {
                            disableAnalytics();
                          }}
                        >
                          <UnsavedChangesProvider>
                            {children}
                          </UnsavedChangesProvider>
                        </CookieBannerProvider>
                      </NextThemesProvider>
                    </NewBenefitProvider>
                  </DateRangeProvider>
                </WorkspaceProvider>
              </UserProvider>
            </AuthFormProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryProvider>
    </GoogleOAuthProvider>
  );
}
